import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaGoogle, FaGithub, FaApple } from 'react-icons/fa';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/apiService';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');
    const [showResendVerification, setShowResendVerification] = useState(false);
    
    const { login, signInWithGoogle, signInWithGithub, signInWithApple, checkEmailVerification } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check for messages from navigation state
    useEffect(() => {
        if (location.state?.message) {
            setInfoMessage(location.state.message);
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setInfoMessage('');
        setShowResendVerification(false);
        setIsSubmitting(true);

        try {
            const userCredential = await login(formData.email, formData.password);
            
            // Save/update user in backend database
            const token = await userCredential.user.getIdToken();
            await ApiService.saveUser(userCredential.user, token);
            
            // Check if email is verified
            const isVerified = await checkEmailVerification();
            
            if (!isVerified) {
                // Redirect to verification page (user stays logged in)
                navigate('/auth/verify-email');
                return;
            }
            
            navigate('/dashboard'); // Redirect to dashboard after successful login
        } catch (error) {
            console.error('Login error:', error);
            switch (error.code) {
                case 'auth/user-not-found':
                    setError('No account found with this email address.');
                    break;
                case 'auth/wrong-password':
                    setError('Incorrect password. Please try again.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address.');
                    break;
                case 'auth/user-disabled':
                    setError('This account has been disabled.');
                    break;
                case 'auth/too-many-requests':
                    setError('Too many failed attempts. Please try again later.');
                    break;
                default:
                    setError('Failed to log in. Please check your credentials and try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSocialLogin = async (provider) => {
        setError('');
        setLoading(true);

        try {
            let result;
            switch (provider) {
                case 'google':
                    result = await signInWithGoogle();
                    break;
                case 'github':
                    result = await signInWithGithub();
                    break;
                case 'apple':
                    result = await signInWithApple();
                    break;
                default:
                    throw new Error('Unknown provider');
            }
            
            if (result.user) {
                // Save/update user in backend database
                const token = await result.user.getIdToken();
                await ApiService.saveUser(result.user, token);
                
                // Social logins are auto-verified, redirect to dashboard
                navigate('/dashboard');
            }
        } catch (error) {
            console.error(`${provider} login error:`, error);
            switch (error.code) {
                case 'auth/popup-closed-by-user':
                    setError('Login was cancelled.');
                    break;
                case 'auth/popup-blocked':
                    setError('Login popup was blocked. Please allow popups for this site.');
                    break;
                case 'auth/account-exists-with-different-credential':
                    setError('An account already exists with the same email address but different sign-in credentials.');
                    break;
                case 'auth/operation-not-allowed':
                    setError(`${provider} sign-in is not enabled. Please contact support.`);
                    break;
                default:
                    setError(`Failed to sign in with ${provider}. Please try again.`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Login - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Sign in to your Nexgen Learning account to access personalized AI-driven education." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main className="min-h-screen bg-gray-50 pt-28 pb-20">
                <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">Welcome To NexGen Learning</h1>
                        <p className="mt-2 text-gray-600">Sign in to your account</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-8">
                        {error && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}
                        
                        {infoMessage && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-600">{infoMessage}</p>
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="your.email@example.com"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    placeholder="••••••••"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="rememberMe"
                                        name="rememberMe"
                                        checked={formData.rememberMe}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        disabled={isSubmitting}
                                    />
                                    <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                                        Remember me
                                    </label>
                                </div>
                                <Link to="/auth/forgot-password" className="text-sm text-primary hover:text-primary-dark">
                                    Forgot password?
                                </Link>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-primary text-white py-3 px-6 rounded-md font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Signing In...' : 'Sign In'}
                            </button>
                        </form>

                        <div className="mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-3 gap-3">
                                <button
                                    onClick={() => handleSocialLogin('google')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaGoogle className="h-5 w-5 text-red-600" />
                                </button>
                                <button
                                    onClick={() => handleSocialLogin('github')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaGithub className="h-5 w-5 text-gray-900" />
                                </button>
                                <button
                                    onClick={() => handleSocialLogin('apple')}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaApple className="h-5 w-5 text-gray-900" />
                                </button>
                            </div>
                        </div>

                        <p className="mt-6 text-center text-sm text-gray-600">
                            Don't have an account?{' '}
                            <Link to="/auth/register" className="text-primary hover:text-primary-dark">
                                Sign up
                            </Link>
                        </p>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
};

export default Login; 